import React, { useEffect, useRef } from "react";
import { FaMicrophone, FaHeadphones, FaMusic, FaCog } from "react-icons/fa";
import { BsDisplay, BsMusicNoteList, BsBroadcast, BsPeople } from "react-icons/bs";
import { motion } from "framer-motion";


const Features = () => {
  const sectionRef = useRef(null);
  const boxesRef = useRef([]);

  const facilities = [
    { icon: <FaMicrophone style={{ fontSize: "1.5rem" }} />, title: "Soundproof Recording Rooms", description: "Professional acoustic treatment for crystal-clear audio" },
    { icon: <FaHeadphones style={{ fontSize: "1.5rem" }} />, title: "Professional Microphone Array", description: "High-end microphones for pristine voice capture" },
    { icon: <FaMusic style={{ fontSize: "1.5rem" }} />, title: "Advanced Mixing Consoles", description: "State-of-the-art mixing and processing equipment" },
    { icon: <FaCog style={{ fontSize: "1.5rem" }} />, title: "High-End Recording Equipment", description: "Industry standard recording and monitoring setup" }
  ];

  const features = [
    { icon: <BsDisplay style={{ fontSize: "1.5rem" }} />, title: "Live Streaming Support", description: "Seamless integration with streaming platforms" },
    { icon: <BsMusicNoteList style={{ fontSize: "1.5rem" }} />, title: "Multi-Track Recording", description: "Professional multi-track recording capabilities" },
    { icon: <BsBroadcast style={{ fontSize: "1.5rem" }} />, title: "AI-Enhanced Processing", description: "Advanced AI-powered audio enhancement" },
    { icon: <BsPeople style={{ fontSize: "1.5rem" }} />, title: "Seamless Guest Connectivity", description: "Remote guest integration with zero latency" }
  ];

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    // Add Intersection Observer for section
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.style.opacity = "1";
          section.style.transform = "scale(1) translateY(0)";
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(section);

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const triggerPosition = viewportHeight * 0.8;

      // Calculate progress based on section's distance from viewport center
      const progress = Math.min(1, Math.max(0, (triggerPosition - rect.top) / viewportHeight));

      boxesRef.current.forEach((box) => {
        if (!box) return;

        const boxIndex = box.dataset.index;
        let transform = "";

        switch (boxIndex) {
          case "1":
            transform = `translateX(${-100 * (1 - progress)}px)`;
            break;
          case "2":
            transform = `translateX(${100 * (1 - progress)}px)`;
            break;
          case "3":
            transform = `translateY(${100 * (1 - progress)}px)`;
            break;
          default:
            transform = "translateX(0)";
            break;
        }

        box.style.transform = transform;
        box.style.opacity = `${progress}`;
        box.style.transition = progress > 0 ? "all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)" : "none";
      });
    };

    // Scroll handler with throttling
    let animationFrame;
    const scrollHandler = () => {
      if (!animationFrame) {
        animationFrame = requestAnimationFrame(() => {
          handleScroll();
          animationFrame = 0;
        });
      }
    };

    window.addEventListener("scroll", scrollHandler);
    handleScroll(); // Initial trigger

    return () => {
      window.removeEventListener("scroll", scrollHandler);
      observer.disconnect();
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, []);

  // Ref collector with cleanup
  const addBoxToRefs = (el) => {
    if (el && !boxesRef.current.includes(el)) {
      boxesRef.current.push(el);
    }
  };

  return (
    <section id="Studio"
      ref={sectionRef}
      style={{
        minHeight: "100vh",
        // backgroundColor: "#000",
        color: "#fff",
        padding: "4rem 1rem",
        textAlign: "center",
        // opacity: 0,
        // transform: "scale(0.95) translateY(30px)",
        // transition: "opacity 0.8s ease-out, transform 0.8s ease-out",
      }}
    >
      <div style={{ 
        maxWidth: "1100px", 
        margin: "0 auto",
        padding: "0 20px" // Added for mobile
      }}>
        {/* Main Heading */}
        <h2
  style={{
    fontSize: "clamp(2rem, 5vw, 2.5rem)", // Responsive font size
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "2px",
    color: "#fdac00",
    marginBottom: "3rem",
    display: "inline-block", // Default for desktop
    textAlign: "center", // Ensure centering works across all devices
  }}
>
  {["Facilities", "&", "Features"].map((word, wordIndex) => (
    <React.Fragment key={wordIndex}>
      <span
        style={{
          display: "inline-block",
          whiteSpace: "nowrap", // Keep words intact on desktop
          marginRight: wordIndex < 2 ? "0.4em" : "0", // Space between words on desktop
          ...(window.innerWidth <= 768 && {
            display: "block", // Stack vertically on mobile
            whiteSpace: "normal",
            width: "auto",
            textAlign: "center",
            marginRight: "0", // Remove horizontal spacing on mobile
            marginBottom: "0.5rem", // Vertical spacing between words on mobile
          }),
        }}
      >
        {word.split("").map((char, charIndex) => (
          <motion.span
            key={charIndex}
            style={{
              display: "inline-block",
              color: "#fdac00", // Yellow color
              padding: "0 0px",
              cursor: "pointer",
              whiteSpace: "normal",
            }}
            whileHover={{ y: -8 }} // Moves up slightly on hover
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
          >
            {char === " " ? "\u00A0" : char} {/* Handles spaces correctly */}
          </motion.span>
        ))}
      </span>
    </React.Fragment>
  ))}
</h2>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", // Responsive grid
          gap: "3rem", 
          textAlign: "left"
        }}>
          {/* Facilities Column */}
          <div>
            <h3 style={{ 
              fontSize: "clamp(1.5rem, 4vw, 2rem)", 
              fontWeight: "bold", 
                  color: "#c31b48", 
              marginBottom: "1.5rem",
              textAlign: "center"
            }}>
              Facilities
            </h3>
            <div style={{ display: "grid", gap: "1.5rem" }}>
              {facilities.map((item, index) => (
                <div
                  key={index}
                  ref={addBoxToRefs}
                  data-index={index % 2 === 0 ? "1" : "2"}
                  style={{
                    backgroundColor: "#1A1A1A",
                    padding: "1.5rem",
                    borderRadius: "10px",
                    border: "1px solid #fdac00",
                    transition: "all 0.3s ease-in-out",
                    transform: index % 2 === 0 ? "translateX(-100px)" : "translateX(100px)",
                    opacity: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    // Mobile styles
                    '@media (max-width: 768px)': {
                      transform: "none !important",
                      opacity: "1 !important",
                      padding: "1rem",
                      flexDirection: "column",
                      textAlign: "center"
                    }
                  }}
                >
                  <div style={{ color: "#4A90E2", fontSize: "2rem" }}>{item.icon}</div>
                  <div>
                    <h4 style={{ 
                      fontSize: "clamp(1.1rem, 3vw, 1.3rem)", 
                      fontWeight: "600", 
                      marginBottom: "0.5rem" 
                    }}>
                      {item.title}
                    </h4>
                    <p style={{ 
                      color: "#bbb",
                      fontSize: "clamp(0.9rem, 2.5vw, 1rem)" 
                    }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Features Column */}
          <div>
            <h3 style={{ 
              fontSize: "clamp(1.5rem, 4vw, 2rem)", 
              fontWeight: "bold", 
                  color: "#c31b48",
             
              marginBottom: "1.5rem",
              textAlign: "center"
            }}>
              Features
            </h3>
            <div style={{ display: "grid", gap: "1.5rem" }}>
              {features.map((item, index) => (
                <div
                  key={index}
                  ref={addBoxToRefs}
                  data-index={index % 2 === 0 ? "1" : "2"}
                  style={{
                    backgroundColor: "#1A1A1A",
                    padding: "1.5rem",
                    borderRadius: "10px",
                    border: "1px solid #fdac00",
                    transition: "all 0.3s ease-in-out",
                    transform: index % 2 === 0 ? "translateX(-100px)" : "translateX(100px)",
                    opacity: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    // Mobile styles
                    '@media (max-width: 768px)': {
                      transform: "none !important",
                      opacity: "1 !important",
                      padding: "1rem",
                      flexDirection: "column",
                      textAlign: "center"
                    }
                  }}
                >
                  <div style={{ color: "#4A90E2", fontSize: "2rem" }}>{item.icon}</div>
                  <div>
                    <h4 style={{ 
                      fontSize: "clamp(1.1rem, 3vw, 1.3rem)", 
                      fontWeight: "600", 
                      marginBottom: "0.5rem" 
                    }}>
                      {item.title}
                    </h4>
                    <p style={{ 
                      color: "#bbb",
                      fontSize: "clamp(0.9rem, 2.5vw, 1rem)" 
                    }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add mobile-specific media queries */}
      <style>
        {`
          @media (max-width: 768px) {
            #Studio {
              padding: 0rem 1rem !important;
            }
            
            .features-grid {
              grid-template-columns: 1fr !important;
              gap: 2rem !important;
            }
            
            .feature-card {
              flex-direction: column !important;
              text-align: center !important;
              padding: 1rem !important;
            }
            
            .feature-icon {
              margin-bottom: 1rem !important;
            }
          }
        `}
      </style>
    </section>
  );
};

export default Features;